/**
 * Wires the `assetsIntangible.errors` copy into `getErrorMessage` (ALC-3487 R5).
 *
 * The copy for these codes was written into `lib/i18n/assets-intangible.ts` in pt-BR and en, and
 * registered — but nothing read it. `getErrorMessage` only consults
 * `lib/validation-error-messages.ts`, so every `INTANGIBLE_PROJECT_*` and
 * `BILL_INTANGIBLE_PROJECT_*` refusal fell through to the server's own `message`. This closes
 * that gap for slice 1's codes as well as slice 2's.
 *
 * Shaped like `getKaminoSyncErrorCopy`/`extractKaminoSyncErrorPayload` so `getErrorMessage` reads
 * the same way for all three families. It differs in one respect on purpose: the copy is looked up
 * through the i18n instance rather than a table local to this file, because the strings already
 * exist in two languages and duplicating them here would guarantee they drift. That also means an
 * en reviewer gets English, which is the other half of what this fixes — the conflict codes were
 * thrown with English text and the pt-BR resource was never consulted.
 */
import i18n from '@/lib/i18n';

/**
 * The families whose codes this module answers for. Anything else is left to the caller.
 *
 * Two prefixes, not a list of sub-families. The list form re-opened the gap this module exists
 * to close once per slice — `INTANGIBLE_ALLOCATION_` (ALC-3488), then `INTANGIBLE_FISCAL_` and
 * `INTANGIBLE_ENTITY_POLICY_` (found by the wave-3 fold review, Lane C M-1), and on inspection
 * `INTANGIBLE_GATE_`, `INTANGIBLE_IN_PROGRESS_` and `INTANGIBLE_WRITE_OFF_` had copy in both
 * languages that nothing could reach either. Every `INTANGIBLE_*` code is raised by the
 * intangibles module and has its copy in `assets-intangible.ts`; a code that turns out to have
 * none falls through to the server's sentence (see `getIntangibleErrorCopy`), so the wide
 * prefix cannot put a raw i18n key on screen. `intangible-error-messages.test.ts` holds the
 * two tables to each other.
 */
const CODE_PREFIXES = ['INTANGIBLE_', 'BILL_INTANGIBLE_'] as const;

/**
 * Codes this module answers for that belong to no `INTANGIBLE_*` family.
 *
 * The first two are raised from the grid's correction path and mean something specific there —
 * a book with no supported functional currency, and no open period to date the reclass into —
 * so the grid's own copy is more use than the generic GL sentence. The other two are the
 * indefinite-life class rules (ADR 0017 D8), raised by the class form and answered here because
 * that is where their copy lives. Listed one by one rather than by prefix so the module cannot
 * start answering for the whole `GL_`/`JOURNAL_`/`ASSET_CLASS_` surface.
 */
const EXACT_CODES = new Set([
  'GL_BOOK_CURRENCY_UNSUPPORTED',
  'JOURNAL_NO_OPEN_PERIOD',
  'ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_INTANGIBLE',
  'ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_NONE',
]);

function isIntangibleErrorCode(code: string): boolean {
  return CODE_PREFIXES.some((prefix) => code.startsWith(prefix)) || EXACT_CODES.has(code);
}

/**
 * Translated copy for an intangible-project error code, or null when there is none.
 *
 * Null on an unknown code rather than the key itself: a raw
 * `assetsIntangible:errors.SOMETHING_NEW` on screen is worse than the server's own sentence, and
 * returning null lets `getErrorMessage` fall through to it.
 */
export function getIntangibleErrorCopy(code: string): string | null {
  if (!isIntangibleErrorCode(code)) {
    return null;
  }
  const key = `assetsIntangible:errors.${code}`;
  const translated = i18n.t(key);
  return typeof translated === 'string' && translated !== key && translated.trim()
    ? translated
    : null;
}

/**
 * Pull a structured intangible-project error payload out of an API error.
 *
 * Mirrors `extractKaminoSyncErrorPayload`, including its two nestings: Nest puts the thrown object
 * on `response.data`, and a `BadRequestException({ code, message })` shows up with the object
 * under `message` when the body was itself wrapped.
 */
export function extractIntangibleErrorCode(err: unknown): string | null {
  const maybeError = err as { response?: { data?: unknown }; details?: unknown };
  const data = (maybeError?.response?.data ?? maybeError?.details) as
    { code?: string; message?: string | { code?: string }; details?: unknown } | undefined;
  if (!data) {
    return null;
  }
  if (typeof data.message === 'object' && data.message?.code) {
    return isIntangibleErrorCode(data.message.code) ? data.message.code : null;
  }
  if (typeof data.code === 'string' && isIntangibleErrorCode(data.code)) {
    return data.code;
  }
  if (data.details) {
    return extractIntangibleErrorCode({ details: data.details });
  }
  return null;
}
